import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
  Scroll,
} from '@angular/router';
import {
  getPath,
  TreeComponent,
  TreeMenuOption,
  TreeNode,
} from '@nx-vigil/core/web/shared/ui';
import { EMPTY, filter, map, switchMap } from 'rxjs';

@Component({
  selector: 'core-master-detail',
  imports: [
    MatSidenavModule,
    RouterModule,
    TreeComponent,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  template: `
    <div class="h-full w-full" #drawerContainer>
      <mat-drawer-container class="bg-2 border-primary h-full w-full">
        <mat-drawer
          class="w-3/12 border-r-2"
          mode="side"
          opened
          [disableClose]="true"
        >
          <div #headerContent>
            <ng-content></ng-content>
          </div>
          <div>
            <core-tree
              [data]="data()"
              [selected]="selectedIdIfExists()"
              [menuOptionsFn]="menuOptionsFn()"
              [scrollHeight]="scrollHeight()"
              [itemSize]="itemSize()"
            /></div
        ></mat-drawer>
        <mat-drawer-content class="w-9/12 px-2">
          @if (selectedIdIfExists()) {
            <router-outlet></router-outlet>
          }
        </mat-drawer-content>
      </mat-drawer-container>
    </div>
  `,
  styles: [
    `
      .mdc-icon-button {
        @apply scale-75;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MasterDetailComponent implements AfterViewInit, OnDestroy {
  public readonly tree = viewChild(TreeComponent);

  public readonly data = input<TreeNode[]>([]);
  public readonly menuOptionsFn = input<(node: TreeNode) => TreeMenuOption[]>();
  public selectedIdChanged = output<string | undefined>();
  private readonly _selectedIdChanged = effect(() => {
    this.selectedIdChanged.emit(this.selectedIdIfExists());
  });
  public readonly itemSize = input<number>(36);

  protected scrollHeight = signal<string>('600px');
  protected drawerContainer =
    viewChild.required<ElementRef<HTMLElement>>('drawerContainer');
  protected headerContent =
    viewChild.required<ElementRef<HTMLElement>>('headerContent');

  private resizeObserver?: ResizeObserver;
  private readonly defaultOffset = 33;

  public ngAfterViewInit() {
    this.calculateScrollHeight();
    this.setupResizeObserver();
  }

  public ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  private setupResizeObserver() {
    this.resizeObserver = new ResizeObserver(() => {
      this.calculateScrollHeight();
    });

    if (this.drawerContainer().nativeElement) {
      this.resizeObserver.observe(this.drawerContainer().nativeElement);
    }

    if (this.headerContent().nativeElement) {
      this.resizeObserver.observe(this.headerContent().nativeElement);
    }
  }

  private calculateScrollHeight() {
    if (this.drawerContainer().nativeElement) {
      const height = this.drawerContainer().nativeElement.offsetHeight;
      const offset = this.headerContent().nativeElement.offsetHeight ?? 0;

      console.log('drawerContainer height:', height);
      console.log('headerContent height:', offset);

      console.log('scroll height:', height - offset - this.defaultOffset);

      this.scrollHeight.set(`${height - offset - this.defaultOffset}px`);
    }
  }

  private readonly router = inject(Router);
  private readonly routerNavigationEvent$ = this.router.events.pipe(
    filter(
      e =>
        e instanceof NavigationEnd ||
        (e instanceof Scroll && e.routerEvent instanceof NavigationEnd),
    ),
  );
  private readonly route = inject(ActivatedRoute);
  protected readonly idFromRoute = toSignal<string>(
    this.routerNavigationEvent$.pipe(
      switchMap(
        () => this.route.firstChild?.params.pipe(map(({ id }) => id)) ?? EMPTY,
      ),
    ),
  );

  protected selectedIdIfExists = computed(() => {
    const id = this.idFromRoute();
    if (id && !getPath(this.data(), id)) return undefined;
    return this.idFromRoute();
  });
}
